import React, { useState, useEffect } from 'react';
import { httpService } from '../../services/http.service';
import { API_ENDPOINTS } from '../../config/api.config';
import DemandesTable from './DemandesTable';
import DemandeDetailsModal from './DemandeDetailsModal';

const DemandesList = () => {
  const [demandes, setDemandes] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [testDataFiltered, setTestDataFiltered] = useState(0);
  const [showTestData, setShowTestData] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1
  });

  // Récupérer les noms des projets
  const fetchProjectNames = async (demandes) => {
    const projectIds = [...new Set(demandes.map(d => d.projet).filter(id => id))];
    const projectsMap = {};
    
    console.log('IDs des projets à récupérer:', projectIds);
    
    for (const projectId of projectIds) {
      try {
        const project = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(projectId));
        console.log(`Projet ${projectId} récupéré:`, project);
        
        // Essayer différents champs possibles pour le nom du projet
        const projectName = project.name || project.title || project.nom || project.project_name || `Projet #${projectId}`;
        projectsMap[projectId] = projectName;
        
        console.log(`Nom du projet ${projectId}:`, projectName);
      } catch (error) {
        console.warn(`Erreur lors de la récupération du projet ${projectId}:`, error);
        projectsMap[projectId] = `Projet #${projectId}`;
      }
    }
    
    console.log('Map des projets:', projectsMap);
    return projectsMap;
  };

  // Filtrer les données de test
  const filterTestData = (demandes) => {
    return demandes.filter(demande => {
      // Exclure les demandes avec des données de test évidentes
      const isTestData = 
        demande.name === 'Test User' ||
        demande.email === 'test@example.com' ||
        demande.adresse === '123 Test Street' ||
        demande.numero_tel === '0123456789' ||
        demande.post_code === 12345;
      
      return !isTestData;
    });
  };

  // Récupérer les demandes
  const fetchDemandes = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await httpService.get(API_ENDPOINTS.DEMANDES.LIST(page));
      console.log('Demandes récupérées:', response);
      
      const allDemandes = response.results || [];
      
      // Filtrer les données de test si nécessaire
      const realDemandes = showTestData ? allDemandes : filterTestData(allDemandes);
      const testDemandesCount = allDemandes.length - realDemandes.length;
      
      console.log(`Demandes filtrées: ${realDemandes.length} réelles, ${testDemandesCount} de test ${showTestData ? 'incluses' : 'exclues'}`);
      
      setDemandes(realDemandes);
      
      // Récupérer les noms des projets
      const projectsMap = await fetchProjectNames(realDemandes);
      setProjects(projectsMap);
      
      setPagination({
        count: realDemandes.length, // Utiliser le nombre de demandes réelles
        next: response.next,
        previous: response.previous,
        currentPage: page
      });
      
      // Afficher un message si des données de test ont été filtrées
      setTestDataFiltered(testDemandesCount);
      if (testDemandesCount > 0) {
        console.warn(`${testDemandesCount} demande(s) de test ont été filtrées et ne sont pas affichées`);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
      setError('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  // Charger les demandes au montage du composant
  useEffect(() => {
    fetchDemandes();
  }, []);

  // Gérer la sélection d'une demande
  const handleDemandeSelect = (demande) => {
    setSelectedDemande(demande);
    setShowModal(true);
  };

  // Fermer le modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDemande(null);
  };

  // Gérer la pagination
  const handlePageChange = (page) => {
    fetchDemandes(page);
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2">Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Erreur</h4>
          <p>{error}</p>
          <button 
            className="btn btn-outline-danger" 
            onClick={() => fetchDemandes()}
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Liste des Demandes</h2>
            <div className="badge bg-primary fs-6">
              {pagination.count} demande{pagination.count > 1 ? 's' : ''}
            </div>
          </div>

          {/* Message informatif sur les données de test filtrées */}
          {testDataFiltered > 0 && (
            <div className="alert alert-info d-flex align-items-center justify-content-between mb-4" role="alert">
              <div className="d-flex align-items-center">
                <i className="fas fa-info-circle me-2"></i>
                <div>
                  <strong>Données de test filtrées :</strong> {testDataFiltered} demande{testDataFiltered > 1 ? 's' : ''} de test {testDataFiltered > 1 ? 'ont été' : 'a été'} exclue{testDataFiltered > 1 ? 's' : ''} de l'affichage.
                  <br />
                  <small>Seules les demandes avec des données réelles sont affichées.</small>
                </div>
              </div>
              <button 
                className="btn btn-outline-info btn-sm"
                onClick={() => {
                  setShowTestData(!showTestData);
                  fetchDemandes(pagination.currentPage);
                }}
                title={showTestData ? "Masquer les données de test" : "Afficher les données de test"}
              >
                <i className={`fas fa-${showTestData ? 'eye-slash' : 'eye'} me-1`}></i>
                {showTestData ? 'Masquer' : 'Afficher'} les données de test
              </button>
            </div>
          )}

          <DemandesTable 
            demandes={demandes}
            projects={projects}
            onDemandeSelect={handleDemandeSelect}
            pagination={pagination}
            onPageChange={handlePageChange}
          />

          {showModal && selectedDemande && (
            <DemandeDetailsModal
              demande={selectedDemande}
              onClose={handleCloseModal}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DemandesList;